import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConnectionProvider } from "./context/ConnectionContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPage from "./pages/PrivacyPage";
import AboutPage from "./pages/AboutPage";
import AuthCallback from "./components/AuthCallback";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminForgotPasswordPage from "./pages/admin/AdminForgotPasswordPage";
import AdminResetPasswordPage from "./pages/admin/AdminResetPasswordPage";
import AdminDashboard from "./pages/admin/AdminDashboard";

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";
import AdminLayout from "./layouts/AdminLayout";

// Components
import CookieConsent from "./components/CookieConsent";

// Dashboard Pages
import ConnectionsPage from "./pages/ConnectionsPage";
import QueryEditorPage from "./pages/QueryEditorPage";
import DatabaseExplorerPage from "./pages/DatabaseExplorerPage";
import QueryHistoryPage from "./pages/QueryHistoryPage";
import SavedQueriesPage from "./pages/SavedQueriesPage";
import MetricsPage from "./pages/MetricsPage";

import "./App.css";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Analytics Tracking Hook
const useAnalyticsTracking = () => {
  useEffect(() => {
    // Only track on public pages (not admin/dashboard)
    const isPublicPage = !window.location.pathname.startsWith('/admin') && 
                         !window.location.pathname.startsWith('/dashboard') &&
                         !window.location.pathname.startsWith('/connections') &&
                         !window.location.pathname.startsWith('/query-editor') &&
                         !window.location.pathname.startsWith('/explorer') &&
                         !window.location.pathname.startsWith('/history') &&
                         !window.location.pathname.startsWith('/saved-queries') &&
                         !window.location.pathname.startsWith('/metrics');
    
    if (!isPublicPage) return;
    
    // Set the API URL for the tracking script
    window.ANALYTICS_API_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Check if script is already loaded
    if (document.getElementById('aq-analytics-script')) return;
    
    // Load the analytics script
    const script = document.createElement('script');
    script.id = 'aq-analytics-script';
    script.src = '/analytics.js';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      // Cleanup if needed
    };
  }, []);
};

// Subscription Context
const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};

const SubscriptionProvider = ({ children }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeader, updateUserCredits } = useAuth();

  const checkSubscription = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/subscription-status`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      setSubscriptionData(response.data);
      if (response.data && updateUserCredits) {
        updateUserCredits(response.data.credits ?? 0);
      }
    } catch (error) {
      console.error("Failed to check subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkSubscription();
    } else {
      setLoading(false);
    }
  }, [user]);

  return (
    <SubscriptionContext.Provider value={{ subscriptionData, loading, checkSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { subscriptionData, loading: subLoading } = useSubscription();
  const location = useLocation();
  
  // Fast-path 1: user data passed directly from AuthCallback via navigation state
  if (location.state?.user) {
    return children;
  }

  // Fast-path 2: JWT token in localStorage means auth is in progress — don't redirect yet
  const hasToken = !!localStorage.getItem("token");
  
  if (authLoading || subLoading || (hasToken && !user)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Subscription required route (for subscribe page - need auth but allow without subscription)
const AuthRequiredRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Router component that handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // Initialize analytics tracking
  useAnalyticsTracking();
  
  // Check URL fragment for session_id synchronously (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/about" element={<AboutPage />} />

      {/* Payment Routes - need auth but not subscription */}
      <Route path="/subscribe" element={
        <AuthRequiredRoute>
          <PaymentPage />
        </AuthRequiredRoute>
      } />
      <Route path="/payment/success" element={
        <AuthRequiredRoute>
          <PaymentSuccessPage />
        </AuthRequiredRoute>
      } />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin/*" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
      </Route>

      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ConnectionsPage />} />
        <Route path="connections" element={<ConnectionsPage />} />
        <Route path="explorer" element={<DatabaseExplorerPage />} />
        <Route path="query" element={<QueryEditorPage />} />
        <Route path="history" element={<QueryHistoryPage />} />
        <Route path="saved" element={<SavedQueriesPage />} />
        <Route path="metrics" element={<MetricsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <ConnectionProvider>
        <BrowserRouter>
          <SubscriptionProvider>
            <div className="dark min-h-screen bg-background">
              <AppRouter />
              <Toaster richColors position="top-right" />
              <CookieConsent />
            </div>
          </SubscriptionProvider>
        </BrowserRouter>
      </ConnectionProvider>
    </AuthProvider>
  );
}

export default App;
